import { ModalHandler } from "@lib/ModalHandler";
import {
  TDistroSectionType,
  TMutationHandler,
  TQuery,
  TQueryArrayNonEmpty,
  TQuestion,
  TQuestionType,
  TSectionData,
} from "@lib/types";
import {
  getNormalizedText,
  isTextInput,
  nodeByText,
  scrollToWithOffset,
  waitForElementToExist,
  waitForElementWithTextToExist,
} from "@lib/util/common";
import { Accessor } from "solid-js";
import {
  dispatchChangeEvents,
  getNextVisibleQuestionContainer,
  getQTargetFromQuestionContainer,
  getQuestionContainer,
  getSaveBtnSafely,
} from "./form_util";

export function getQuestionHandler(
  handlerType: "radio",
  qNumber: number,
  questionText: TQuery,
  answerText?: TQuery
): RadioQuestion | null;

export function getQuestionHandler(
  handlerType: "textinput",
  qNumber: number,
  questionText: TQuery,
  answerText?: string
): TextInputQuestion | null;

export function getQuestionHandler(
  handlerType: TQuestionType,
  qNumber: number,
  questionText: TQuery,
  answerText?: TQuery | string
) {
  try {
    // eslint-disable-next-line unicorn/prefer-ternary
    if (handlerType === "radio") {
      return new RadioQuestion(qNumber, questionText, answerText);
    } else {
      // For textinput, answerText must be a string or undefined
      if (answerText !== undefined && typeof answerText !== "string") {
        throw new Error("TextInputQuestion only accepts string values for answerText");
      }
      return new TextInputQuestion(qNumber, questionText, answerText);
    }
  } catch {
    return null;
  }
}

class RadioQuestion implements TQuestion {
  questionElement: HTMLElement;
  answerElements: HTMLElement[];
  questionText: TQuery;
  private answerText: TQuery | undefined;

  constructor(qNumber: number, questionText: TQuery, answerText?: TQuery) {
    const questionElement = _getQTextElement(qNumber, [questionText]);
    if (questionElement === null) {
      throw new Error(`Cannot find q. ${qNumber}: ${questionText.toString()}`);
    }

    this.questionText = questionText;
    this.questionElement = questionElement;

    const answersContainerElement = _getAnswerContainerElement(qNumber, questionElement);
    if (answersContainerElement === null) {
      throw new Error(`Cannot find corresponding answers for q. ${qNumber}`);
    }

    this.answerElements = [...answersContainerElement.children] as HTMLElement[];
    this.answerText = answerText;
  }

  answer(answerText?: TQuery): boolean {
    if (answerText === undefined) {
      return this.answerWithText(this.answerText);
    }

    return this.answerWithText(answerText);
  }

  private answerWithText(answerText: TQuery | undefined): boolean {
    if (answerText === undefined) {
      console.error("Predefined answer text is undefined");
      return false;
    }

    let isSuccess = false;

    for (const elm of this.answerElements) {
      const elementText = elm.innerText.trim();
      const isMatch =
        answerText instanceof RegExp
          ? answerText.test(elementText)
          : elementText.toLowerCase() === answerText.toLowerCase();

      if (isMatch) {
        elm.click();
        isSuccess = true;
      }
    }

    return isSuccess;
  }
}

class TextInputQuestion implements TQuestion {
  questionElement: HTMLElement;
  textInputElement: HTMLTextAreaElement | HTMLInputElement;
  questionText: TQuery;
  private answerText: string | undefined;

  constructor(qNumber: number, questionText: TQuery, answerText?: string) {
    const questionElement = _getQTextElement(qNumber, [questionText]);
    if (questionElement === null) {
      throw new Error(`Cannot find q. ${qNumber}: ${questionText.toString()}`);
    }

    this.questionText = questionText;
    this.questionElement = questionElement;

    const textInputElement = _getAnswerContainerElement(qNumber, questionElement);
    if (textInputElement === null) {
      throw new Error(`Cannot find corresponding answers for q. ${qNumber}`);
    }

    if (textInputElement instanceof HTMLTextAreaElement || textInputElement instanceof HTMLInputElement) {
      this.textInputElement = textInputElement;
    } else {
      throw new TypeError("Cannot find child textarea or text input element");
    }

    this.answerText = answerText;
  }

  private answerWithText(answerText: string | undefined): boolean {
    if (answerText === undefined) {
      console.error("Predefined answer text is undefined");
      return false;
    }

    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    console.log(`Answering question "${this.questionText}" with text: "${answerText}"`);
    this.textInputElement.value = answerText;
    console.log(`Text input value set to: "${this.textInputElement.value}"`);

    dispatchChangeEvents(this.textInputElement);

    return true;
  }

  answer(answerText?: string): boolean {
    if (answerText === undefined) {
      return this.answerWithText(this.answerText);
    }

    return this.answerWithText(answerText);
  }
}

export async function waitForSOMainMenu(
  modalHandler: Accessor<ModalHandler>,
  timeoutSeconds = 25,
  throwIfMissing = true
): Promise<boolean> {
  console.log("Waiting for SO main menu");
  modalHandler().context.setStatusHeader("Entering SO Main Menu. Waiting for page...");

  const element = await waitForElementToExist(() => nodeByText("#actions > div", /complete your actions/i));
  if (throwIfMissing && element === null) {
    throw new Error(`SO main menu took too long to load. Exceeding timeout of ${timeoutSeconds} seconds.`);
  }

  return element === null ? false : true;
}

export async function waitForRegularSectionPage(
  modalHandler: Accessor<ModalHandler>,
  sectionName: RegExp | string,
  throwIfMissing: boolean,
  timeoutSeconds = 25
) {
  console.log("Waiting for Section page");
  modalHandler().context.setStatusHeader(
    `Entering regular section page. ${String(sectionName)} Waiting for page...`
  );

  const element = await waitForElementToExist(() => document.querySelector("#actionHeader"));
  if (element === null && throwIfMissing) {
    throw new Error(
      `Section page '${String(sectionName)}' took too long to load. Exceeding timeout of ${timeoutSeconds} seconds.`
    );
  } else if (element === null) {
    return null;
  }

  modalHandler().context.setStatusHeader("Section page found!");
  console.log("Section page found!");

  return null;
}

export async function waitForDistroPageGenerally(
  modalHandler: Accessor<ModalHandler>,
  sectionName: RegExp | string,
  _throwIfMissing: boolean,
  timeoutSeconds = 4
): Promise<TDistroSectionType> {
  modalHandler().context.setStatusHeader(`Entering Distro page. ${String(sectionName)} Waiting for page...`);

  console.warn("_throwIfMissing is not used in waitForDistroPage");

  const element = await waitForElementToExist(() => document.querySelector("#IAAlertList"), timeoutSeconds);
  if (element !== null) {
    modalHandler().context.setStatusHeader("Distro page found!");
    return "distro-regular";
  }

  const distroSectionHeader = document.querySelector(".sortAlerteWrap")?.parentElement;
  if (distroSectionHeader == null) {
    modalHandler().addErrorMsg("Cannot find Distro section header");
    return null;
  }

  if (distroSectionHeader.innerText.includes("Choose Department")) {
    modalHandler().context.setStatusHeader("Distro Choose Department page found!");
    return "distro-choose-department";
  }

  return null;
}

export async function waitForDistroPageStrict(
  modalHandler: Accessor<ModalHandler>,
  sectionName: RegExp | string,
  throwIfMissing: boolean,
  timeoutSeconds = 4
): Promise<boolean> {
  modalHandler().context.setStatusHeader(`Waiting for Distro page: ${String(sectionName)} ...`);

  console.warn("_throwIfMissing is not used in waitForDistroPage");

  const element = await waitForElementToExist(() => document.querySelector("#IAAlertList"), timeoutSeconds);
  if (element !== null) {
    modalHandler().context.setStatusHeader("Distro page found!");
    return true;
  }

  if (throwIfMissing) {
    throw new Error(`Distro page '${String(sectionName)}' not found`);
  }

  return false;
}

export async function waitForChooseDeptDistroPageStrict(
  modalHandler: Accessor<ModalHandler>,
  sectionName: RegExp | string,
  throwIfMissing: boolean,
  timeoutSeconds = 10
): Promise<boolean> {
  modalHandler().context.setStatusHeader(`Waiting for Department page: ${String(sectionName)} ...`);

  const element = await waitForElementWithTextToExist(".pnl-h", "Choose Department", timeoutSeconds);
  if (element !== null) {
    modalHandler().context.setStatusHeader("Distro Choose Department page found!");
    return true;
  }

  if (throwIfMissing) {
    throw new Error(`Distro Choose Department page '${String(sectionName)}' not found`);
  }

  return false;
}

export async function waitForDistroItemPage(modalHandler: Accessor<ModalHandler>, timeoutSeconds = 25) {
  console.log("Waiting for Distro Item page");

  const element = await waitForElementToExist(() => document.querySelector("#IAQs"));
  if (element === null) {
    throw new Error(
      `Distro Item page took too long to load. Exceeding timeout of ${timeoutSeconds} seconds.`
    );
  }

  modalHandler().context.setStatusHeader("");
  console.log("Distro Item page found!");
}

export function handleEnterOnTextInput(e: KeyboardEvent) {
  if (e.key !== "Enter") return;

  if (!(e.target instanceof HTMLInputElement)) return;

  // Must be an <input> inside #dataEntry with id like "q1", "q2", etc.
  if (!e.target.closest("#dataEntry") || !e.target.id.startsWith("q")) {
    return;
  }

  e.preventDefault();
  const nextVisibleQuestionContainer = getNextVisibleQuestionContainer(e.target);

  if (nextVisibleQuestionContainer === null) {
    e.target.blur();
    scrollToWithOffset(getSaveBtnSafely());
    return;
  }

  const nextInput = getQTargetFromQuestionContainer(nextVisibleQuestionContainer);
  if (nextInput instanceof HTMLInputElement && isTextInput(nextInput)) {
    nextInput.focus();
  } else if (nextInput instanceof HTMLTextAreaElement) {
    nextInput.focus();
  } else {
    e.target.blur();
    scrollToWithOffset(nextVisibleQuestionContainer, { scrollSpeed: 2 });
  }
}

export const autoScrollToNextAvailableQuestion: TMutationHandler = (mutation) => {
  if (
    mutation.type === "attributes" &&
    mutation.target instanceof HTMLElement &&
    mutation.target.id.startsWith("qfc") &&
    document.activeElement instanceof HTMLElement &&
    getQuestionContainer(document.activeElement) !== getQuestionContainer(mutation.target)
  ) {
    console.log("scrolling to elemet", mutation.target);
    scrollToWithOffset(mutation.target, { scrollSpeed: 2 });
  }
};

export const autoScrollToNextAfterPhotoUpload: TMutationHandler = (mutation) => {
  if (mutation.type !== "attributes" || !(mutation.target instanceof HTMLElement)) {
    return;
  }

  // Get the question container that contains this element
  const questionContainer = getQuestionContainer(mutation.target);
  if (questionContainer === null || !questionContainer.id.startsWith("qwrap")) {
    return;
  }

  // Check if this is a photo upload container (qtype11)
  if (!questionContainer.classList.contains("qtype11")) {
    return;
  }

  // Check if the photo has been uploaded by looking for uploadedPhoto elements in the photos container
  const photosContainer = questionContainer.querySelector("[id^='photos']");
  if (!photosContainer) {
    return;
  }

  const uploadedPhotos = photosContainer.querySelectorAll(".uploadedPhoto");
  if (uploadedPhotos.length === 0) {
    return; // No photos uploaded yet
  }

  // Photo has been uploaded, scroll to the next available question
  const nextQuestionContainer = getNextVisibleQuestionContainer(questionContainer);
  if (nextQuestionContainer) {
    scrollToWithOffset(nextQuestionContainer, { scrollSpeed: 2 });
  }
};

function _getQTextElement(qNumber: number, requiredLines: TQueryArrayNonEmpty): HTMLElement | null {
  const targetSelector = ".qText";
  const qTextElements = document.querySelectorAll<HTMLElement>(targetSelector);

  for (const qElm of qTextElements) {
    // const normalizedText = qElm.innerText.replaceAll(/ *\n+ */g, "\n");
    const normalizedText = getNormalizedText(qElm.innerText);

    // Split into non-empty, trimmed lines
    const lines = normalizedText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    if (lines.length === 0) continue;

    // Check question number prefix on first line
    const questionNumberPattern = new RegExp(`^${qNumber}\\.`);
    if (!questionNumberPattern.test(lines[0])) continue;

    // Compare each i-th required line to the i-th actual line
    let allMatch = true;
    for (const [i, requiredLine] of requiredLines.entries()) {
      if (i === 0) continue; // skip q. number

      const isMatch =
        requiredLine instanceof RegExp ? requiredLine.test(lines[i]) : lines[i] === requiredLine;

      if (isMatch) {
        allMatch = false;
        break;
      }
    }

    if (allMatch) {
      return qElm;
    }
  }

  return null;
}

async function waitForAnySectionPage(
  modalHandler: Accessor<ModalHandler>,
  sectionTitle: string
): Promise<TDistroSectionType> {
  modalHandler().context.setStatusHeader(`Waiting for Section page: ${sectionTitle} ...`);

  const element = await waitForElementToExist(
    () => nodeByText(".pnl-h", /choose department/i) ?? document.querySelector("#IAAlertList")
  );

  if (element !== null) {
    return element.id === "IAAlertList" ? "distro-regular" : "distro-choose-department";
  }

  return null;
}

export async function handleGoFowardFromMainDistroSection(
  modalHandler: Accessor<ModalHandler>,
  sectionData: TSectionData
): Promise<boolean> {
  sectionData.sectionElement.click();
  modalHandler().context.setStatusHeader(`Waiting for Distro items page: ${sectionData.title}`);

  const distroSectionType = await waitForAnySectionPage(modalHandler, sectionData.title);
  if (distroSectionType === "distro-regular") return true;

  const deptSelectContainer = document.querySelector("#alertCompletionCaption")?.parentElement;
  if (deptSelectContainer == null) {
    throw new Error("Could not find Distro 'Choose Department' page: dept selection container not found");
  }

  for (const deptSelect of deptSelectContainer.querySelectorAll<HTMLAnchorElement>("a.list-btn")) {
    const deptSelectFirstLine = deptSelect.innerText.toLowerCase().split("\n")[0].trim();
    if (deptSelectFirstLine !== "all departments") continue;

    deptSelect.click();
    const result = await waitForDistroPageStrict(modalHandler, sectionData.title, true, 10);
    if (!result) throw new Error("Could not find Distro page after selecting department");
    return true;
  }

  return false;
}

// for when the distro page we are on is uncertain: we may be one or two pages deep
export async function handleBackAwayFromDistroSection(
  modalHandler: Accessor<ModalHandler>,
  sectionData: TSectionData
): Promise<boolean> {
  clickBackButton();
  modalHandler().context.setStatusHeader(`Navigating back from Distro section: ${sectionData.title}`);

  const element = await waitForElementToExist(
    () => nodeByText(".pnl-h", /choose department/i) ?? nodeByText("#actions > div", /complete your actions/i)
  );

  if (element === null) return false;
  if (element.parentElement?.id === "actions") return true;

  clickBackButton();

  const result = await waitForSOMainMenu(modalHandler, 10, false);
  if (result) modalHandler().context.setStatusHeader("Successfully returned to main menu");

  return false;
}

export function clickSaveButton() {
  nodeByText("button.btn", "Save")!.click();
}

export function clickBackButton() {
  document.getElementById("backbtn")!.click();
}

function _getAnswerContainerElement(questionNumber: number, qTextElement: HTMLElement): HTMLElement | null {
  return qTextElement.parentElement!.querySelector(`[id='q${questionNumber}']`);
}
